import React, { useEffect, useState } from "react";
import {
  ThemeProvider,
  createTheme,
  styled,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  Divider,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Chip,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useLocation, useNavigate, Outlet, matchPath } from "react-router-dom";
import {
  ArrowBackIos as ArrowBackIosIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  AddCircle as AddCircleIcon,
  Forum as ForumIcon,
} from "@mui/icons-material";
import { fetchCategories } from "../MockApi";
import KisanLogo from "../../Assets/Logo/kisan-bg.png";
import LogOutDropDown from "../../LogOut/logOutDropDown";
import axios from "axios";
import ArticleIcon from "@mui/icons-material/Article";
const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  height: "100vh",
  overflowY: "auto",
  backgroundColor: "#212930",
  [theme.breakpoints.down("md")]: {
    paddingTop: `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(
      4
    )})`,
  },
}));
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "black",
    color: "white",
    fontSize: 12,
  },
});
const drawerMobileWidth = 280;
const CategoryViewLayout = () => {
  const baseUrl = process.env.REACT_APP_HOST_API;
  const theme = useTheme();
  const navigate = useNavigate();
  // const location = useLocation();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // const isAuthenticated = localStorage.getItem('isAuthenticated');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Get from localStorage
    const local = localStorage.getItem("isAuthenticated") === "true";

    // Get from query params (reactive to URL change)
    const params = new URLSearchParams(location.search);
    const query = params.get("isAuthenticated")?.toLowerCase() === "true";

    // Merge both
    setIsAuthenticated(local || query);
  }, [location.search]);

  const [open, setOpen] = useState(!isMobile);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const loadCategories = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${baseUrl}/forum/categories-aggregation`
        );
        const aggregatedCategories = response?.data?.data || [];
        let categoriesToUse = aggregatedCategories;
        const totalCategoriesCount = 10;
        if (aggregatedCategories.length < totalCategoriesCount) {
          const allResponse = await axios.get(`${baseUrl}/forum/categories`);
          categoriesToUse = allResponse?.data?.data || [];
        }
        const totalPostCount = aggregatedCategories.reduce(
          (sum, cat) => sum + (cat.totalPosts || 0),
          0
        );
        const allPostsCategory = {
          _id: "all",
          name: "All Posts",
          totalPosts: totalPostCount,
          color: "#2F6CC1",
        };

        const categoriesWithAll = [allPostsCategory, ...categoriesToUse];

        setCategories(categoriesWithAll);
        setFilteredCategories(categoriesWithAll);
      } catch (error) {
        console.error("Failed to load categories:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      setFilteredCategories(
        categories.filter((cat) =>
          cat.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredCategories(categories);
    }
  }, [searchQuery, categories]);

  // useEffect(() => setOpen(isLargeScreen), [isLargeScreen]);

  const handleDrawerToggle = () => setOpen(!open);
  const handleSearchChange = (event) => setSearchQuery(event.target.value);
  const handleCategoryClick = (categoryId, categoryName) => {
    if (categoryId === "all") {
      navigate("/ForumsCategory");
    } else {
      navigate(`/ForumsCategory/category/${categoryId}/${categoryName}`);
    }
    if (isMobile) {
      setOpen(false);
    }
  };
  const handleCreatePostClick = () => {
    navigate("/ForumsCategory/create-post");
    if (isMobile) {
      setOpen(false);
    }
  };
  const handleNavigateToMyPosts = () => {
    navigate("/ForumsCategory?Myposts=true");
  };

  const categoryMatch = matchPath(
    "/ForumsCategory/category/:categoryId/:categoryName",
    location.pathname
  );
  const currentCategoryId = categoryMatch?.params.categoryId;
  const isAllPostsPage =
    location.pathname === "/ForumsCategory" ||
    location.pathname === "/ForumsCategory/";
  const renderSkeleton = () => (
    <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
      <List>
        {[...Array(11)].map((_, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton
              sx={{
                justifyContent: "initial",
                px: 2.5,
                minHeight: 48,
                py: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: 12 }}
                />
              </Box>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        mt: isMobile ? 4 : 0,
      }}
    >
      {/* Top Section */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            justifyContent: open || isMobile ? "space-between" : "center",
          }}
        >
          <IconButton onClick={handleDrawerToggle} sx={{ color: "#9fb4c7" }}>
            {open ? (
              <ArrowBackIosIcon sx={{ display: { xs: "none", md: "block" } }} />
            ) : (
              <MenuIcon sx={{ display: { xs: "none", md: "block" } }} />
            )}
          </IconButton>
          {open && !isMobile && <img src={KisanLogo} alt="logo" width={120} />}
        </Box>
        <Divider sx={{ backgroundColor: "#555" }} />
        <Box sx={{ p: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ display: open || isMobile ? "block" : "none" }}
          />
        </Box>
      </Box>

      {/* Scrollable Category List (No changes here) */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List>
          {loading
            ? renderSkeleton()
            : filteredCategories.map((item) => {
                const isActive = isAllPostsPage
                  ? item._id === "all"
                  : item._id.toString() === currentCategoryId;

                const listItem = (
                  <ListItem key={item._id} disablePadding>
                    <ListItemButton
                      sx={{
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                        borderRight: isActive ? "4px solid #1976d2" : "none",
                        backgroundColor: isActive ? "#2e3b4a" : "transparent",
                        minHeight: 48,
                        py: 0,
                      }}
                      onClick={() => handleCategoryClick(item._id, item.name)}
                    >
                      {/* THIS CONTENT IS CRITICAL FOR THE SIDEBAR TO APPEAR */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        {open && (
                          <ListItemText
                            primary={item.name}
                            sx={{ color: "#fff" }}
                          />
                        )}
                        <Chip
                          icon={open ? <ForumIcon /> : null}
                          label={
                            open ? `${item.totalPosts || 0} posts` : item.name
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: item.color,
                            color: item.color,
                            "& .MuiChip-icon": { color: item.color },
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );

                return !isMobile ? (
                  <CustomTooltip title={item?.name} key={item._id}>
                    {listItem}
                  </CustomTooltip>
                ) : (
                  listItem
                );
              })}
        </List>
      </Box>

      <Box>
        <Divider sx={{ backgroundColor: "#555" }} />
        {isAuthenticated && (
          <Box sx={{ p: open ? 2 : 0.5, textAlign: "center" }}>
            {open ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <CustomTooltip title="Create Post" placement="right">
                  <Button
                    variant="contained"
                    startIcon={<AddCircleIcon sx={{ color: "#fff" }} />}
                    fullWidth
                    onClick={handleCreatePostClick}
                    sx={{
                      backgroundColor: "#2F6CC1",
                      color: "#fff",
                      borderRadius: 5,
                    }}
                  >
                    Create Post
                  </Button>
                </CustomTooltip>
                <CustomTooltip title="My Post" placement="right">
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleNavigateToMyPosts()}
                    sx={{
                      backgroundColor: "#2F6CC1",
                      color: "#fff",
                      borderRadius: 5,
                    }}
                  >
                    My posts
                  </Button>
                </CustomTooltip>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <CustomTooltip title="Create Post" placement="right">
                  <IconButton color="primary" onClick={handleCreatePostClick}>
                    <AddCircleIcon />
                  </IconButton>
                </CustomTooltip>
                <CustomTooltip title="My Posts" placement="right">
                  <IconButton color="primary" onClick={handleNavigateToMyPosts}>
                    <ArticleIcon />
                  </IconButton>
                </CustomTooltip>
              </Box>
            )}
          </Box>
        )}
        <Divider sx={{ backgroundColor: "#555" }} />
        {/* <LogOutDropDown /> */}
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={createTheme({ palette: { mode: "dark" } })}>
      <Box
        sx={{ display: "flex", height: "100vh", backgroundColor: "#212930" }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            display: { xs: "block", md: "none" },
            backgroundColor: "#181F26",
            borderBottom: "1px solid #555",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
            {/* You now have space for a title! */}
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, textAlign: "center" }}
            >
              Forums360
            </Typography>
            {/* Add a spacer to keep the title centered if needed */}
            <Box sx={{ width: 48 }} />
          </Toolbar>
        </AppBar>

        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#212930",
              color: "#9fb4c7",
              width: isMobile
                ? drawerMobileWidth
                : open
                ? 330
                : { xs: 90, sm: 140 },
              transition: "width 0.3s",
              overflowX: "hidden",
              position: isMobile ? "fixed" : "relative",
              borderRight: "1px solid #555",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <MainContent>
          <Outlet />
        </MainContent>
      </Box>
    </ThemeProvider>
  );
};

export default CategoryViewLayout;
